/**
 * The `client` key of a result record (#1376). `navigator.connection` keeps
 * its values as prototype getters, so copying the object itself serialized to
 * `{}`, which RTDB drops: no result ever stored the network speed. The four
 * values are copied out instead, read when the result starts rather than when
 * the app loaded, next to `online`.
 *
 * `readConnection` is the app's only reader of `navigator.connection`; the
 * feedback report uses it too. minute-qset-v4 keeps its own copy for its log
 * events, which write an unreported value as `null` (its log schema), where a
 * result omits it.
 */

export interface ConnectionLike {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

const CONNECTION_FIELDS: ReadonlyArray<keyof ConnectionLike> = [
  "effectiveType",
  "downlink",
  "rtt",
  "saveData",
];

interface NavigatorLike {
  onLine: boolean;
  connection?: object;
}

/**
 * Plain copy of the values the browser reports (RTDB rejects `undefined`);
 * `undefined` without the Network Information API (Safari, Firefox).
 */
export const readConnection = (nav: {
  connection?: object;
}): ConnectionLike | undefined => {
  const {connection} = nav;
  if (!connection) return undefined;
  return Object.fromEntries(
    CONNECTION_FIELDS.flatMap((field) => {
      const value = (connection as Record<string, unknown>)[field];
      return value === undefined || value === null ? [] : [[field, value]];
    }),
  ) as ConnectionLike;
};

export interface ResultClient {
  connection?: ConnectionLike;
  online: boolean;
}

// Safari and Firefox have no Network Information API: no key at all, so the
// record never claims a reading it does not have.
export const toResultClient = <T extends object>(
  base: T,
  nav: NavigatorLike = navigator,
): T & ResultClient => {
  const connection = readConnection(nav);
  return {...base, ...(connection ? {connection} : {}), online: nav.onLine};
};
